import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { useMovieStore } from './movieStore';

export type UserRole = 'system' | 'admin' | 'editor' | 'viewer';

export interface User {
  id: string;
  email: string;
  name: string;
  surname: string;
  fullName: string;
  role: UserRole;
}

export interface AuthState {
  // State
  user: User | null;
  accessToken: string | null;
  refreshToken: string | null;
  isAuthenticated: boolean;

  // Actions
  setAuth: (user: User, accessToken: string, refreshToken: string) => void;
  logout: () => void;
  updateTokens: (accessToken: string, refreshToken: string) => void;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set) => ({
      // Initial state
      user: null,
      accessToken: null,
      refreshToken: null,
      isAuthenticated: false,

      // Actions
      setAuth: (user, accessToken, refreshToken) =>
        set({
          user,
          accessToken,
          refreshToken,
          isAuthenticated: true,
        }),

      logout: () =>
        {
          useMovieStore.getState().unloadMovie();
          set({
            user: null,
            accessToken: null,
            refreshToken: null,
            isAuthenticated: false,
          });
        },

      updateTokens: (accessToken, refreshToken) =>
        set({
          accessToken,
          refreshToken,
        }),
    }),
    {
      name: 'prototype-auth', // localStorage key
      partialize: (state) => ({
        user: state.user,
        accessToken: state.accessToken,
        refreshToken: state.refreshToken,
        isAuthenticated: state.isAuthenticated,
      }),
    }
  )
);

// Selectors
export const selectUserRole = (state: AuthState) => state.user?.role ?? null;
export const selectIsSystem = (state: AuthState) => state.user?.role === 'system';
export const selectIsAdmin = (state: AuthState) => state.user?.role === 'admin' || state.user?.role === 'system';
export const selectIsEditor = (state: AuthState) => state.user?.role === 'editor' || state.user?.role === 'admin' || state.user?.role === 'system';
export const selectIsViewer = (state: AuthState) => state.user?.role === 'viewer';
export const selectCanAccessAdmin = (state: AuthState) => state.user?.role === 'admin' || state.user?.role === 'system';

// Helper: Kullanıcının belirli bir role sahip olup olmadığını kontrol eder
export const hasRole = (user: User | null, roles: UserRole[]): boolean => {
  if (!user) return false;
  return roles.includes(user.role);
};
